/* Native theme enhancement: no frontend framework and no third-party runtime. */
(() => {
  'use strict';

  const messages = JSON.parse(
    document.querySelector('[data-rr-translations]')?.textContent || '{}'
  );
  const message = key => messages[key] || key;
  const copyTimers = new WeakMap();
  let pending = false;
  const rootUrl = () => window.Shopify?.routes?.root || '/';

  function updateHero(hero) {
    const width = hero.getBoundingClientRect().width;
    const scale = width < 768
      ? Math.min(.68, Math.max(.48, width / 700))
      : Math.min(1, width / 1512);

    hero.style.setProperty('--rr-scene-scale', String(scale));

    const purchase = hero.querySelector('.rr-hero-purchase');
    if (purchase) {
      hero.style.setProperty(
        '--rr-purchase-height',
        `${purchase.offsetHeight}px`
      );
    }

    hero.rrCarousel?.render();
  }

  function createCarousel(hero) {
    const photos = [...hero.querySelectorAll('[data-rr-angle]')].map(node => ({
      node,
      angle: Number(node.dataset.rrAngle) || 0,
      opacity: Number(getComputedStyle(node).opacity),
      originalStyle: {
        left: node.style.left,
        top: node.style.top,
        transform: node.style.transform,
        opacity: node.style.opacity
      }
    }));

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );
    const enabled = hero.dataset.loopEnabled === 'true' && photos.length > 1;
    const duration = Math.max(
      56,
      Math.min(224, Number(hero.dataset.loopDuration) || 112)
    ) * 1000;

    const angles = photos.map(photo => photo.angle);
    const low = Math.min(0, ...angles);
    const high = Math.max(0, ...angles);
    const span = Math.max(140, high - low + 20);
    const start = (low + high - span) / 2;
    const bounds = hero.getBoundingClientRect();

    let offset = 0;
    let previousTime = null;
    let frame = null;
    let focused = false;
    let selected = false;
    let visible = bounds.bottom > 0 && bounds.top < window.innerHeight;
    let destroyed = false;

    const removers = [];
    const listen = (target, type, handler) => {
      target?.addEventListener(type, handler);
      removers.push(() => target?.removeEventListener(type, handler));
    };

    function render() {
      const radius = Number(hero.dataset.radius) || 850;
      const apex = Number(hero.dataset.apex) || 215;

      photos.forEach(photo => {
        const degrees = enabled && !reducedMotion.matches
          ? ((photo.angle + offset - start) % span + span) % span + start
          : photo.angle;

        const radians = degrees * Math.PI / 180;
        const x = 756 + radius * Math.sin(radians) - 95;
        const y = apex + radius - radius * Math.cos(radians) - 120;

        // Cards recycle only after fading at the ends of the clipped arc.
        const edge = Math.min(degrees - start, start + span - degrees);
        const fade = enabled && !reducedMotion.matches
          ? Math.min(1, Math.max(0, edge / 10))
          : 1;

        photo.node.style.left = '0px';
        photo.node.style.top = '0px';
        photo.node.style.transform =
          `translate3d(${x}px,${y}px,0) rotate(${degrees}deg)`;
        photo.node.style.opacity = String(
          photo.opacity * fade * fade * (3 - 2 * fade)
        );
      });
    }

    function stop() {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      previousTime = null;
      hero.dataset.rrCarouselRunning = 'false';
    }

    function tick(time) {
      if (previousTime !== null) {
        offset = (
          offset + Math.min(100, time - previousTime) * span / duration
        ) % span;
      }

      previousTime = time;
      render();
      frame = requestAnimationFrame(tick);
    }

    function sync() {
      if (destroyed) return;

      const runnable = enabled &&
        !reducedMotion.matches &&
        !focused &&
        !selected &&
        visible &&
        !document.hidden;

      if (!runnable) {
        stop();
      } else if (frame === null) {
        hero.dataset.rrCarouselRunning = 'true';
        frame = requestAnimationFrame(tick);
      }
    }

    listen(hero, 'focusin', () => {
      focused = true;
      sync();
    });

    listen(hero, 'focusout', event => {
      focused = !!event.relatedTarget && hero.contains(event.relatedTarget);
      sync();
    });

    listen(hero, 'shopify:block:select', () => {
      selected = true;
      sync();
    });

    listen(hero, 'shopify:block:deselect', () => {
      selected = false;
      sync();
    });

    listen(document, 'visibilitychange', sync);

    listen(reducedMotion, 'change', () => {
      offset = 0;
      render();
      sync();
    });

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        sync();
      })
      : null;

    observer?.observe(hero);
    render();
    sync();

    return {
      render,
      destroy() {
        destroyed = true;
        stop();
        observer?.disconnect();
        removers.forEach(remove => remove());
        photos.forEach(photo => {
          Object.assign(photo.node.style, photo.originalStyle);
        });
      }
    };
  }

  function createAccordion(list) {
    const items = [...list.querySelectorAll('.rr-faq-item')];
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

    let current = items.find(item => item.open) || null;
    let desired = current;
    let working = false;
    let destroyed = false;

    const animations = new Set();
    const removers = [];

    items.forEach(item => {
      if (item !== current) item.open = false;
      item.dataset.rrExpanded = String(item.open);
    });

    async function transition(item, opening) {
      const summary = item.querySelector('summary');
      const answer = item.querySelector('.rr-faq-answer');
      const start = item.getBoundingClientRect().height;

      if (opening) item.open = true;
      item.dataset.rrExpanded = String(opening);

      if (!item.animate || reducedMotion.matches) {
        item.open = opening;
        return;
      }

      const border = parseFloat(
        getComputedStyle(item).borderTopWidth
      ) || 0;

      const end = summary.getBoundingClientRect().height +
        border +
        (opening ? answer.getBoundingClientRect().height : 0);

      item.style.height = `${start}px`;
      item.style.overflow = 'hidden';

      const options = {
        duration: 240,
        easing: 'cubic-bezier(0,0,.2,1)'
      };

      const height = item.animate(
        [{ height: `${start}px` }, { height: `${end}px` }],
        options
      );

      const fade = answer.animate(
        [
          { opacity: opening ? 0 : 1 },
          { opacity: opening ? 1 : 0 }
        ],
        options
      );

      animations.add(height);
      animations.add(fade);

      try {
        await Promise.all([height.finished, fade.finished]);
      } catch (_) {
        /* Section removal cancels both animations. */
      } finally {
        animations.delete(height);
        animations.delete(fade);
        item.open = opening;
        item.style.height = '';
        item.style.overflow = '';
      }
    }

    async function sync() {
      if (working || destroyed) return;
      working = true;

      // Finish closing before opening the next item: never two open answers.
      while (!destroyed && current !== desired) {
        if (current) {
          await transition(current, false);
          current = null;
        }

        if (destroyed) break;

        if (desired) {
          current = desired;
          await transition(current, true);
        }
      }

      working = false;
    }

    items.forEach(item => {
      const summary = item.querySelector('summary');

      const click = event => {
        event.preventDefault();
        desired = desired === item ? null : item;
        sync();
      };

      const keydown = event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        if (!event.repeat) click(event);
      };

      summary.addEventListener('click', click);
      summary.addEventListener('keydown', keydown);

      removers.push(() => summary.removeEventListener('click', click));
      removers.push(() => summary.removeEventListener('keydown', keydown));
    });

    return {
      open(item) {
        if (items.includes(item)) {
          desired = item;
          sync();
        }
      },
      destroy() {
        destroyed = true;
        removers.forEach(remove => remove());
        animations.forEach(animation => animation.cancel());
      }
    };
  }

  function createSubmenu(header) {
    const nav = header.querySelector('.rr-submenu');
    const section = header.closest('.f-section-header');
    const main = header.querySelector(
      ':scope > .container-fluid, :scope > .container'
    );

    if (!nav || !section || !main) return null;

    let referenceY = Math.max(0, window.scrollY);
    let frame = 0;
    let hidden = false;
    let destroyed = false;

    section.classList.add('rr-submenu-header-section');

    function measure() {
      if (destroyed) return;

      const style = getComputedStyle(nav);
      const expandedNav =
        parseFloat(style.getPropertyValue('--rr-submenu-height')) +
        parseFloat(style.getPropertyValue('--rr-submenu-gap'));

      const currentNav =
        nav.getBoundingClientRect().height +
        parseFloat(style.marginTop);

      const height =
        header.getBoundingClientRect().height +
        expandedNav -
        currentNav;

      section.style.setProperty('--rr-header-expanded-height', `${height}px`);
      header.dataset.rrExpandedHeight = String(height);

      header.style.setProperty(
        '--rr-main-bar-height',
        `${height - parseFloat(
          style.getPropertyValue('--rr-submenu-height')
        )}px`
      );
    }

    function show() {
      hidden = false;
      header.classList.remove('rr-submenu-hidden');
      nav.inert = false;
      nav.removeAttribute('aria-hidden');
    }

    function render() {
      frame = 0;

      // Clamp rubber-band scrolling at both ends to avoid spurious direction changes.
      const maximum = Math.max(
        0,
        document.documentElement.scrollHeight - innerHeight
      );
      const y = Math.min(maximum, Math.max(0, window.scrollY));

      if (y <= 8) {
        show();
        referenceY = y;
        return;
      }

      if (Math.abs(y - referenceY) < 6) return;

      hidden = y > referenceY;
      header.classList.toggle('rr-submenu-hidden', hidden);
      nav.inert = hidden;

      if (hidden) nav.setAttribute('aria-hidden', 'true');
      else nav.removeAttribute('aria-hidden');

      referenceY = y;
    }

    function schedule() {
      if (!frame) frame = requestAnimationFrame(render);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    nav.addEventListener('focusin', show);

    const observer = 'ResizeObserver' in window
      ? new ResizeObserver(measure)
      : null;

    observer?.observe(main);

    document.fonts?.ready.then(() => {
      if (header.isConnected) measure();
    });

    measure();

    return {
      destroy() {
        destroyed = true;
        cancelAnimationFrame(frame);
        observer?.disconnect();

        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', measure);
        nav.removeEventListener('focusin', show);

        show();

        delete header.dataset.rrExpandedHeight;
        header.style.removeProperty('--rr-main-bar-height');
        section.classList.remove('rr-submenu-header-section');
        section.style.removeProperty('--rr-header-expanded-height');
      }
    };
  }

  function init(scope = document) {
    scope.querySelectorAll('.rr-header-with-submenu').forEach(header => {
      if (!header.rrSubmenu) {
        header.rrSubmenu = createSubmenu(header);
      }
    });

    scope.querySelectorAll('[data-rr-faq]').forEach(list => {
      if (list.rrAccordion) return;
      list.rrAccordion = createAccordion(list);
    });

    scope.querySelectorAll('[data-rr-hero]').forEach(hero => {
      if (hero.dataset.rrInitialized) return;

      hero.dataset.rrInitialized = 'true';
      hero.rrCarousel = createCarousel(hero);
      updateHero(hero);

      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(() => updateHero(hero));
        observer.observe(hero);

        const purchase = hero.querySelector('.rr-hero-purchase');
        if (purchase) observer.observe(purchase);

        hero.rrObserver = observer;
      }
    });
  }

  function report(form, message, isError = false) {
    const status = form.querySelector('[data-rr-status]');
    if (!status) return;

    status.textContent = message;
    status.dataset.error = String(isError);

    const hero = form.closest('[data-rr-hero]');

    if (hero) {
      hero.style.minHeight = '';

      if (isError) {
        requestAnimationFrame(() => {
          const purchase = hero.querySelector('.rr-hero-purchase');

          if (purchase) {
            hero.style.minHeight =
              `${purchase.offsetTop + purchase.offsetHeight + 24}px`;
          }
        });
      }
    }
  }

  async function refreshCount() {
    try {
      const response = await fetch(`${rootUrl()}cart.js`, {
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) return;

      const cart = await response.json();

      document.querySelectorAll('[data-rr-cart-count]').forEach(node => {
        node.textContent = cart.item_count;
        node.hidden = cart.item_count === 0;

        node.closest('[data-rr-cart]')?.setAttribute(
          'aria-label',
          `${message('cart')} (${cart.item_count})`
        );
      });
    } catch (_) {
      /* Cart navigation remains available if the counter cannot refresh. */
    }
  }

  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-rr-buy-form]');
    if (!form || !window.fetch) return;

    event.preventDefault();
    if (pending) return;

    const button = form.querySelector('[name=add]');
    if (!button || button.disabled) return;

    pending = true;

    const text = button.querySelector('[data-rr-button-text]');
    const label = button.dataset.rrLabel;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    text.textContent = message('adding');
    report(form, '');

    let added = false;

    try {
      const data = new FormData(form);
      const drawer = document.querySelector('cart-drawer');
      const sections = [];

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', {
          bubbles: true,
          detail: { sections }
        })
      );

      if (drawer?.getSectionsToRender) {
        sections.push(
          ...drawer.getSectionsToRender(true).map(item => item.id)
        );
      }

      if (sections.length) {
        data.set('sections', [...new Set(sections)].slice(0, 5).join(','));
        data.set('sections_url', window.location.pathname);
      }

      const response = await fetch(`${rootUrl()}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: data
      });

      let result;

      try {
        result = await response.json();
      } catch (_) {
        throw new Error(message('uncertain'));
      }

      if (!response.ok || result.status) {
        throw new Error(
          result.description || result.message || message('failed')
        );
      }

      added = true;
      report(form, message('added'));

      document.dispatchEvent(
        new CustomEvent('product-ajax:added', {
          detail: { product: result }
        })
      );

      if (
        drawer &&
        result.sections &&
        Object.values(result.sections).every(Boolean) &&
        typeof drawer.renderContents === 'function'
      ) {
        try {
          // Zest renders the returned cart sections and opens its existing drawer.
          if (
            window.FoxThemeEvents &&
            typeof PUB_SUB_EVENTS !== 'undefined'
          ) {
            window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, result);
          } else {
            drawer.renderContents(result, true, true);
          }

          if (window.FoxTheme?.CartDrawer?.openDrawer) {
            window.FoxTheme.CartDrawer.openDrawer();
          }
        } catch (_) {
          document.dispatchEvent(
            new CustomEvent('cart:refresh', {
              detail: { open: true }
            })
          );
        }

        refreshCount();
      } else {
        window.location.assign(`${rootUrl()}cart`);
      }
    } catch (error) {
      // Never retry an uncertain POST automatically: it could add a second item.
      if (!added) {
        report(
          form,
          error instanceof TypeError ? message('uncertain') : error.message,
          true
        );
      }
    } finally {
      pending = false;

      const purchase = form.closest('[data-rr-purchase]');
      const variants = JSON.parse(
        purchase.querySelector('[data-rr-variants]').textContent
      );
      const variant = variants.find(
        item => String(item.id) === form.elements.id.value
      );

      button.disabled = !variant?.available;
      button.removeAttribute('aria-busy');
      text.textContent = variant?.available ? label : message('unavailable');
    }
  });

  document.addEventListener('change', event => {
    const select = event.target.closest('[data-rr-variant]');
    if (!select) return;

    const purchase = select.closest('[data-rr-purchase]');
    const variants = JSON.parse(
      purchase.querySelector('[data-rr-variants]').textContent
    );
    const variant = variants.find(
      item => String(item.id) === select.value
    );

    if (!variant) return;

    document.querySelectorAll('[data-rr-purchase]').forEach(target => {
      if (target.dataset.productId !== purchase.dataset.productId) return;

      const form = target.querySelector('form');
      form.elements.id.value = variant.id;

      const picker = target.querySelector('[data-rr-variant]');
      if (picker) picker.value = variant.id;

      const button = form.querySelector('[name=add]');
      button.disabled = !variant.available;
      button.querySelector('[data-rr-button-text]').textContent =
        variant.available ? button.dataset.rrLabel : message('unavailable');

      report(form, '');
    });

    document.querySelectorAll('[data-rr-offer]').forEach(offer => {
      if (offer.dataset.productId !== purchase.dataset.productId) return;

      const prices = JSON.parse(
        offer.querySelector('[data-rr-offer-variants]').textContent
      );
      const selectedPrice = prices.find(
        item => String(item.id) === String(variant.id)
      );

      if (!selectedPrice) return;

      offer.querySelector('[data-rr-offer-price]').innerHTML =
        selectedPrice.price;

      const regular = offer.querySelector('[data-rr-regular-price]');
      if (regular) regular.innerHTML = selectedPrice.regular;
    });
  });

  async function copyCode(button) {
    if (button.getAttribute('aria-busy') === 'true') return;

    const label = button.querySelector('[data-rr-copy-label]');
    const status = button.closest('.rr-section')?.querySelector(
      '[data-rr-copy-status]'
    );
    const originalLabel = button.dataset.rrOriginalLabel || label.textContent;

    button.dataset.rrOriginalLabel = originalLabel;
    clearTimeout(copyTimers.get(button));
    button.setAttribute('aria-busy', 'true');

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(button.dataset.rrCopyCode);
      } else {
        // Fallback for browsers without the Clipboard API; triggered by a user click.
        const input = document.createElement('textarea');
        input.value = button.dataset.rrCopyCode;
        input.setAttribute('readonly', '');
        input.style.cssText = 'position:fixed;top:0;left:-9999px';
        document.body.append(input);

        try {
          input.select();
          if (!document.execCommand('copy')) {
            throw new Error('Copy unavailable');
          }
        } finally {
          input.remove();
          button.focus({ preventScroll: true });
        }
      }

      label.textContent = message('copied');
      if (status) status.textContent = message('copySuccess');

      copyTimers.set(button, setTimeout(() => {
        label.textContent = originalLabel;
        if (status) status.textContent = '';
      }, 2400));
    } catch (_) {
      label.textContent = originalLabel;
      if (status) status.textContent = message('copyFailed');
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', event => {
    const copy = event.target.closest('[data-rr-copy-code]');
    if (copy) copyCode(copy);

    const link = event.target.closest('.rr-mobile-menu a');
    if (link) link.closest('details').open = false;

    const cart = event.target.closest('[data-rr-cart]');

    if (cart && window.FoxTheme?.CartDrawer?.openDrawer) {
      event.preventDefault();
      window.FoxTheme.CartDrawer.openDrawer();
    }
  });

  document.addEventListener('shopify:section:load', event => init(event.target));

  document.addEventListener('shopify:block:select', event => {
    const item = event.target.closest('.rr-faq-item');
    item?.closest('[data-rr-faq]')?.rrAccordion?.open(item);
  });

  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.rr-header-with-submenu').forEach(header => {
      header.rrSubmenu?.destroy();
      delete header.rrSubmenu;
    });

    event.target.querySelectorAll('[data-rr-faq]').forEach(list => {
      list.rrAccordion?.destroy();
      delete list.rrAccordion;
    });
  });

  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-rr-hero]').forEach(hero => {
      hero.rrObserver?.disconnect();
      hero.rrCarousel?.destroy();
      delete hero.rrCarousel;
      delete hero.dataset.rrInitialized;
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
})();